import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './OverflowTooltip.css';

type OverflowTooltipProps = {
    text: string;
    tooltipText?: string;
    className?: string;
    as?: 'h2' | 'h3' | 'h4' | 'p' | 'span';
};

const OverflowTooltip: React.FC<OverflowTooltipProps> = ({
    text,
    tooltipText,
    className = '',
    as: Tag = 'span',
}) => {
    const ref = useRef<HTMLElement>(null);
    const hideTimerRef = useRef<number | null>(null);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});

    const fullTooltipText = (tooltipText || text).trim();

    const clearHideTimer = useCallback(() => {
        if (hideTimerRef.current !== null) {
            window.clearTimeout(hideTimerRef.current);
            hideTimerRef.current = null;
        }
    }, []);

    const checkOverflow = useCallback(() => {
        const el = ref.current;
        if (!el) return;

        const visualOverflow =
            el.scrollHeight > el.clientHeight + 1 ||
            el.scrollWidth > el.clientWidth + 1;
        const hasExtendedText = fullTooltipText.length > text.trim().length;

        const overflowing = visualOverflow || hasExtendedText;
        setIsOverflowing(overflowing);
        if (!overflowing) {
            setShowTooltip(false);
        }
    }, [text, fullTooltipText]);

    const updateTooltipPosition = useCallback(() => {
        const el = ref.current;
        if (!el) return;

        const rect = el.getBoundingClientRect();
        const maxWidth = Math.min(420, window.innerWidth - 24);
        const maxHeight = Math.min(window.innerHeight * 0.7, 480);
        const left = Math.min(
            Math.max(12, rect.left),
            window.innerWidth - maxWidth - 12
        );

        const spaceBelow = window.innerHeight - rect.bottom - 12;
        const spaceAbove = rect.top - 12;
        const showAbove = spaceBelow < 180 && spaceAbove > spaceBelow;

        setTooltipStyle({
            left,
            top: showAbove ? rect.top - 8 : rect.bottom + 8,
            maxWidth,
            maxHeight,
            transform: showAbove ? 'translateY(-100%)' : undefined,
        });
    }, []);

    useEffect(() => {
        checkOverflow();
        const el = ref.current;
        if (!el) return;

        const resizeObserver = new ResizeObserver(checkOverflow);
        resizeObserver.observe(el);
        window.addEventListener('resize', checkOverflow);

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', checkOverflow);
            clearHideTimer();
        };
    }, [text, fullTooltipText, checkOverflow, clearHideTimer]);

    useEffect(() => {
        if (!showTooltip) return;

        const hideTooltip = () => setShowTooltip(false);
        window.addEventListener('scroll', hideTooltip, true);
        window.addEventListener('resize', hideTooltip);

        return () => {
            window.removeEventListener('scroll', hideTooltip, true);
            window.removeEventListener('resize', hideTooltip);
        };
    }, [showTooltip]);

    const handleShow = () => {
        if (!isOverflowing) return;
        clearHideTimer();
        updateTooltipPosition();
        setShowTooltip(true);
    };

    const handleHide = () => {
        clearHideTimer();
        hideTimerRef.current = window.setTimeout(() => {
            setShowTooltip(false);
        }, 120);
    };

    return (
        <>
            <Tag
                ref={(node) => {
                    ref.current = node;
                }}
                className={`overflow-tooltip-wrap ${className}`.trim()}
                onMouseEnter={handleShow}
                onMouseLeave={handleHide}
                onFocus={handleShow}
                onBlur={handleHide}
                tabIndex={isOverflowing ? 0 : undefined}
            >
                {text}
            </Tag>
            {showTooltip &&
                isOverflowing &&
                createPortal(
                    <span
                        className="overflow-tooltip-popup"
                        role="tooltip"
                        style={tooltipStyle}
                        onMouseEnter={clearHideTimer}
                        onMouseLeave={handleHide}
                    >
                        {fullTooltipText}
                    </span>,
                    document.body
                )}
        </>
    );
};

export default OverflowTooltip;
